import { memo } from 'react';

import clsx from 'clsx';
import { Field, FieldArray, FormikContextType, FormikProvider } from 'formik';
import { MousePointerClick, Plus, X } from 'lucide-react';

import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import TextField from '@mui/material/TextField';

import css from './ButtonsFormControl.module.css';

import { BUTTON_STUB } from 'v2/constants';
import { FormButton, WithClassName, WithFormButtons } from 'v2/types';

interface ButtonsFormControlProps<T extends WithFormButtons> extends WithClassName {
  formik: FormikContextType<T>;
  initialValue?: FormButton;
  required?: boolean;
}

const _ButtonsFormControl = <T extends WithFormButtons>({
  className,
  formik,
  initialValue = BUTTON_STUB,
  required,
}: ButtonsFormControlProps<T>) => (
  <div className={clsx(css.buttonsFormControl, className)}>
    <p className={css.buttonsFormControlTitle}>Buttons {required && '*'}</p>

    <FormikProvider value={formik}>
      <FieldArray name="buttons">
        {({ push, remove, form }) => (
          <div className="form">
            {form.values.buttons.map((_: FormButton, i: number) => (
              <div key={i} className={css.buttonCard}>
                <div className={css.buttonCardTitleContainer}>
                  <p className={css.buttonCardTitle}>
                    <MousePointerClick width={16} height={16} />
                    {i + 1} Button
                  </p>

                  <IconButton color="error" onClick={() => remove(i)}>
                    <X width={18} height={18} />
                  </IconButton>
                </div>

                <div className={css.buttonCardFields}>
                  <Field as={TextField} name={`buttons.${i}.name`} label="Title" size="small" fullWidth required />

                  <Field as={TextField} name={`buttons.${i}.link`} label="Link" size="small" fullWidth required />
                </div>
              </div>
            ))}

            <Button variant="outlined" onClick={() => push(initialValue)} fullWidth>
              <Plus width={18} height={18} /> Button
            </Button>
          </div>
        )}
      </FieldArray>
    </FormikProvider>
  </div>
);

export const ButtonsFormControl = memo(_ButtonsFormControl) as typeof _ButtonsFormControl;
